/**
 * Invoice Management - Main App
 * ==============================
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { LanguageProvider, useLang } from './shared/i18n';
import { AuthProvider, useAuth } from './shared/context/AuthContext';
import { SocketProvider } from './shared/context/SocketContext';
import { allTranslations } from './i18n';
import { config } from './utils/config';

// Pages
import InvoiceList from './pages/InvoiceList';
import InvoiceDetail from './pages/InvoiceDetail';
import RFQInvoices from './pages/RFQInvoices';
import Settings from './pages/Settings';

// Global styles
import './App.scss';

const AppContent: React.FC = () => {
    const { isAuthenticated, isLoading } = useAuth();
    const { t } = useLang();

    // Show loading while checking auth
    if (isLoading) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
                <p>{t('loading')}</p>
            </div>
        );
    }

    // Not authenticated - show message with redirect option
    if (!isAuthenticated) {
        return (
            <div className="loading-container">
                <div className="error-card">
                    <h2>{t('authRequired')}</h2>
                    <p>{t('authMessage')}</p>
                    <button
                        className="btn-login"
                        onClick={() => {
                            const currentUrl = window.location.origin;
                            window.location.href = `${config.PORTAL_URL}/signin?redirect=${encodeURIComponent(currentUrl)}`;
                        }}
                    >
                        {t('goToPortal')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <Routes>
            <Route path="/" element={<RFQInvoices />} />
            <Route path="/rfq-invoices" element={<RFQInvoices />} />
            <Route path="/invoices" element={<InvoiceList />} />
            <Route path="/invoices/:id" element={<InvoiceDetail />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
};

function App() {
    return (
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <LanguageProvider translations={allTranslations}>
                <AuthProvider
                    apiUrl={config.API_URL}
                    portalUrl={config.PORTAL_URL}
                >
                    <SocketProvider socketUrl={config.API_URL}>
                        <AppContent />
                    </SocketProvider>
                </AuthProvider>
            </LanguageProvider>
        </Router>
    );
}

export default App;
